import { useEffect, useRef } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import PetszelReportingBanner from '../../../components/PetszelReporting/PetszelReportingBanner';
import { useShelterState } from '../../../hooks/useShelter';
import {
  useBillingShippingActions,
  useBillingShippingState,
} from '../../../hooks/useAccountBillingShipping';
import Banner from '../../../components/Banner';
import { Link } from 'react-router';
import { WindowWithGigya } from '../../../services/gigya';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const gigyaInitialized = useRef(false);
  const billingShippingActions = useBillingShippingActions();
  const billingShippingData = useBillingShippingState();
  const { enablePetszelPromotion } = useFeatureFlags();

  const { getBillingShipping } = billingShippingActions ?? {};

  // Fetch billing and shipping information when the component mounts
  useEffect(() => {
    if (getBillingShipping && typeof getBillingShipping === 'function') {
      getBillingShipping();
    }
  }, [getBillingShipping]);

  useEffect(() => {
    // set page title
    document.title = t(TranslationKeyEnum.Settings);
  }, [t]);

  // Initialize Gigya screen set
  useEffect(() => {
    const win = window as unknown as WindowWithGigya;
    if (win.gigya?.accounts && !gigyaInitialized.current) {
      gigyaInitialized.current = true;
      win.gigya.accounts?.showScreenSet?.({
        screenSet: 'CLP-ProfileUpdate',
        startScreen: 'fsl_profileupdatescreen',
        containerID: 'gigya-container',
        lang: window.fsl_locale?.split('-')[0] ?? 'en',
      });
    }
  }, []);

  const locale = window.fsl_locale || 'en-us';
  const hillspetBaseUrl = import.meta.env.VITE_HILLSPET_BASE_URL ?? 'https://www.hillspet.com';
  const localePrefix = locale !== 'en-us' ? `/${locale}` : '';
  const contractUrl = `${hillspetBaseUrl}${localePrefix}/food-shelter-love/log-in?resource=${localePrefix}/food-shelter-love/my-shelter/program-contract`;

  return (
    <div className="flex max-w-[628px] w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-detail-150 font-bold text-black/90">
          {t(TranslationKeyEnum.AccountInformation)}
        </h2>
        <p className="text-detail-100 font-medium text-black/90 max-w-[518px]">
          {t(TranslationKeyEnum.EditEmailContactLink)}{' '}
          <Link to="/help" className="underline">
            {t(TranslationKeyEnum.ContactUs).toLowerCase()}
          </Link>
        </p>
        {/* placeholder for future account information content */}
        <div className="border border-black/20 rounded-200 border-dashed">
          <div id="gigya-container" data-testid="gigya-container" className="min-h-[300px]"></div>
        </div>
      </div>

      <hr className="my-6" />

      <div className="flex flex-col gap-2">
        <h2 className="text-detail-150 font-bold text-black/90">
          {t(TranslationKeyEnum.ProgramContract)}
        </h2>
        <div className="h-fit border border-black/20 rounded-200 flex justify-between items-start gap-3 p-6">
          <div className="flex flex-col gap-2">
            <p className="text-detail-100 font-medium text-black/90">
              <span className="font-bold">{t(TranslationKeyEnum.ViewOrRenewContractLabel)}</span>{' '}
              {t(TranslationKeyEnum.ViewOrRenewContractDescription)}
            </p>
            <p className="text-detail-100 font-medium text-black/90">
              {t(TranslationKeyEnum.ContractAssistance)}{' '}
              <Link to="/help" className="underline">
                {t(TranslationKeyEnum.ContactUs).toLowerCase()}
              </Link>
              .
            </p>
          </div>
          <a
            href={contractUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold shrink-0"
            style={{ color: 'rgba(0, 84, 164, 1)' }}
          >
            {t(TranslationKeyEnum.ViewContract)}
          </a>
        </div>
      </div>

      <hr className="my-6" />

      {shelter && !shelter.is_petszel_enabled && enablePetszelPromotion && (
        <>
          <PetszelReportingBanner isDismissable={false} isPermananentVisible={true} />{' '}
          <hr className="my-6" />{' '}
        </>
      )}

      <div className="flex flex-col gap-2">
        <h2 className="text-detail-150 font-bold text-black/90">
          {t(TranslationKeyEnum.BillingInformation)}
        </h2>
        <p className="text-detail-100 font-medium text-black/90 max-w-[518px]">
          {t(TranslationKeyEnum.EditPaymentMethod)}{' '}
          <Link to="/help" className="underline">
            {t(TranslationKeyEnum.ContactUs).toLowerCase()}
          </Link>
        </p>

        {billingShippingData?.billingInfo && (
          <div className="h-fit border border-black/20 rounded-200 flex flex-col gap-3 p-6">
            <p className="font-semibold text-black">{billingShippingData.billingInfo?.name}</p>
            <p>{billingShippingData.billingInfo?.address}</p>
            <p>
              {billingShippingData.billingInfo?.city}, {billingShippingData.billingInfo?.state}{' '}
              {billingShippingData.billingInfo?.zip}
            </p>
            <p> {t(TranslationKeyEnum.AccountNumber, shelter?.sold_to ?? '-')}</p>
          </div>
        )}
        {/* fallback if no billing info */}
        {!billingShippingData?.billingInfo && (
          <Banner variant="warning" title={t(TranslationKeyEnum.NoBillingInformation)} />
        )}
      </div>

      <hr className="my-6" />

      <div className="flex flex-col gap-2">
        <h2 className="text-detail-150 font-bold text-black/90">
          {t(TranslationKeyEnum.AllShippingAddresses)}
        </h2>
        <p className="text-detail-100 font-medium text-black/90 max-w-[518px]">
          {t(TranslationKeyEnum.EditShippingAddress)}{' '}
          <Link to="/help" className="underline">
            {t(TranslationKeyEnum.ContactUs).toLowerCase()}
          </Link>
        </p>

        {billingShippingData?.shipTos && billingShippingData.shipTos.length > 0 ? (
          billingShippingData.shipTos.map((shipTo, index) => (
            <div
              key={index}
              className="h-fit border border-black/20 rounded-200 flex flex-col gap-3 p-6"
            >
              <p className="font-semibold text-black">{shipTo.name}</p>
              <p>{shipTo.address}</p>
              <p>
                {shipTo.city}, {shipTo.state} {shipTo.zip}
              </p>
              <p>{t(TranslationKeyEnum.ShipToNumber, Number(shipTo.shipTo))}</p>
            </div>
          ))
        ) : (
          // fallback if no shipping addresses
          <Banner variant="warning" title={t(TranslationKeyEnum.NoShippingAddresses)} />
        )}
      </div>
    </div>
  );
};

export default Settings;
